const PERFORMANCE_MAP = {
  '1-Day': 'oneDayPerformance',
  '5-Day': 'weeklyPerformance',
  '3-Month': 'qtrPerformance',
  '6-Month': 'biannualPerformance',
  '1-Year': 'annualPerformance',
  '3-Year': 'threeYearPerformance',
  '5-Year': 'fiveYearPerformance',
  'YTD': 'ytdPerformance',
  'MTD': 'monthToDatePerformance',
};

// Cache for transformed data
const transformedDataCache = new Map();

export const getMdbData = (arrayStockDataMaster, arrayStockMarketData, component, day, chartCategory) => {
  const performanceKey = PERFORMANCE_MAP[day];
  if (!performanceKey || typeof arrayStockDataMaster !== 'object') {
    return { topLabels: [], topData: [] };
  }

  const symbols = arrayStockDataMaster[component] || [];
  
  // Create a lookup map from arrayStockMarketData for O(1) access
  const marketDataMap = arrayStockMarketData.reduce((acc, item) => {
    acc[item.SYMBOL] = item;
    return acc;
  }, {});

  // Filter and sort in one pass
  const result = [];
  for (const symbol of symbols) {
    const data = marketDataMap[symbol];
    if (data && data[performanceKey] != null) {
      result.push({
        label: symbol,
        data: parseFloat(data[performanceKey])
      });
    }
  }
  
  // Sort descending
  result.sort((a, b) => b.data - a.data);

  // Handle different component types
  let finalResult;
  if (component === 'Nifty 50' && chartCategory === 'all') {
    finalResult = result.slice(0, 50);
  } else if (component === 'Sectoral Indices') {
    finalResult = result;
  } else {
    finalResult = chartCategory === 'top' ? result.slice(0, 10) : result.slice(-10);
  }

  return {
    topLabels: finalResult.map(i => i.label),
    topData: finalResult.map(i => i.data)
  };
};

export const transformMdbData = (stockMarketData, dateObj) => {
  const {
    maxDate,
    previousDate,
    weeklyDate,
    qtrDate,
    halfYearDate,
    threeYearDate,
    fiveYearDate,
    maxPreviousYearDate,
    lastDayOfPreviousYear,
    lastDayOfPreviousMonth
  } = dateObj;

  // Create a lookup map for faster access
  const dateValues = stockMarketData.reduce((acc, { SYMBOL, VALUE_DATE, CLOSE_VALUE }) => {
    if (!acc[SYMBOL]) acc[SYMBOL] = {};
    acc[SYMBOL][VALUE_DATE] = CLOSE_VALUE;
    return acc;
  }, {});

  // Pre-calculate performance function
  const calculatePerformance = (currentVal, compareVal) => {
    if (!currentVal || !compareVal || currentVal === compareVal) return null;
    return (((currentVal - compareVal) / compareVal) * 100).toFixed(2);
  };

  // Transform data
  return Object.entries(dateValues).map(([symbol, values]) => {
    const get = (date) => values[date] ?? null;
    
    const maxCloseValue = get(maxDate);
    const lastYearCloseValue = get(lastDayOfPreviousYear);
    const lastMonthCloseValue = get(lastDayOfPreviousMonth);

    return {
      SYMBOL: symbol,
      oneDayPerformance: calculatePerformance(maxCloseValue, get(previousDate)),
      weeklyPerformance: calculatePerformance(maxCloseValue, get(weeklyDate)),
      monthToDatePerformance: calculatePerformance(maxCloseValue, lastMonthCloseValue),
      qtrPerformance: calculatePerformance(maxCloseValue, get(qtrDate)),
      biannualPerformance: calculatePerformance(maxCloseValue, get(halfYearDate)),
      ytdPerformance: calculatePerformance(maxCloseValue, lastYearCloseValue),
      threeYearPerformance: calculatePerformance(maxCloseValue, get(threeYearDate)),
      fiveYearPerformance: calculatePerformance(maxCloseValue, get(fiveYearDate)),
      annualPerformance: calculatePerformance(maxCloseValue, get(maxPreviousYearDate))
    };
  });
};

export const processMarketData = (stockMarketData, dateObj, arrayStockDataMaster, component, day, chartCategory) => {
  const transformed = transformMdbData(stockMarketData, dateObj);
  const { topLabels, topData } = getMdbData(arrayStockDataMaster, transformed, component, day, chartCategory);
  
  return {
    topLabels, 
    topData,
    transformed,
  };
};